import {Logger} from '@meprism/app-utils'
import {createAsyncThunk, createSlice, createSelector} from '@reduxjs/toolkit'
import {API} from 'aws-amplify'
import {useSelector} from 'react-redux'
import {Toast} from '../components/toast/toast'
import {MEPRISM_PRICING_INFO} from '../config/constants'
import {RootState} from './store'
import {useAppSelector} from './storeExports'
import { FetchExecutiveUsers } from '@meprism/shared/src/redux/profile/ProfileSlice'

export type BillingStatus = 
  | 'invoice-generated' 
  | 'paid' 
  | 'unpaid' 
  | 'expired' 
  | null;

export type InviteStatus = 
    | "queued"
    | "invited"
    | "accepted"
    | "expired"
    | "failed"
    | "deleted"
    | "active"

export type CorporateEmployee = {
	active: boolean
	muid: string | null
	email: string
	invite_status:
		| 'invited'
		| 'queued'
		| 'deleted'
		| 'accepted'
		| 'expired'
		| 'failed'
		|  'active'
	invite_time: string
	brokers_found: number
	brokers_in_progress: number
	brokers_removed: number
	billing_status: BillingStatus
	last_scan_date: string
}

export type executiveEmployeeStats = {
	brokers_found: number,
	brokers_removed: number,
	brokers_in_progress: number,
	brokers_in_new: number
}

export type ExecutiveCorporateEmployee = {
	muid: string,
	email: string,
	name: string,
	stats: executiveEmployeeStats,
	billing_status: BillingStatus,
	invite_status: InviteStatus,
}

export type CorporateUserStats = {
	total_employees_sites_in_found: number
	total_employees_sites_in_new: number
	total_employees_sites_in_removed: number
	total_employees_sites_in_removing: number
	total_employees_brokers_in_found: number
	total_employees_brokers_in_new: number
	total_employees_brokers_in_removed: number
	total_employees_brokers_in_removing: number
	total_participating_employees: number
	total_pending_employees: number
}
export type CorpStats = {
  all_time: {
    found: number
    removed: number
    optout_submitted: number
  }
  last_7_days: {
    records_in_progress: number
    clean_scans: number
    brokers_monitored: number
  }
  last_30_days: {
    records_in_progress: number
    clean_scans: number
    brokers_monitored: number
  }
  monthly_trends: {
    month: string
    found: number
    removed: number
    in_progress: number
  }[]
  chart_data: {
    labels: string[]
    found: number[]
    removed: number[]
    optout_in_progress: number[]
  }
} | null


export interface CorporateState {
	UserStatus: CorporateUserStats
	CorpStats: CorpStats | null
	employees: CorporateEmployee[]
	executiveEmployees: ExecutiveCorporateEmployee[]
	code: undefined | CorporateCode
	modalOpen: boolean
}

export type CorporateCode = {
	active: boolean
	allocated: number
	code: string
	create_time: string
	creator_arn: string
	descriptions: string
	duration: number
	duration_type: string
	end_date: string | null
	entitlements: string[]
	exporation_date: string
	limit: number
	owner_buid: string
	title: string
	update_time: string
	_id: string
	cost_per_employee_cents: number,
	is_login_enabled?: boolean | null
}

export const initialCorporateState: CorporateState = {
	UserStatus: {
		total_employees_sites_in_found: 0,
		total_employees_sites_in_new: 0,
		total_employees_sites_in_removed: 0,
		total_employees_sites_in_removing: 0,
		total_employees_brokers_in_found: 0,
		total_employees_brokers_in_new: 0,
		total_employees_brokers_in_removed: 0,
		total_employees_brokers_in_removing: 0,
		total_participating_employees: 0,
		total_pending_employees: 0,
	},
	CorpStats: null,
	employees: [] as CorporateEmployee[],
	code: undefined,
	modalOpen: false,
	executiveEmployees: [] as ExecutiveCorporateEmployee[]
}

const getUsers = async (code: string): Promise<any> => {
	return API.get('Corporate', `/codes/${code}/users`, {})
}
const getUserStats = async (isExecutive: boolean, code: string): Promise<any> => {
	return API.get('Corporate', `/corporate/${code}/users/stats?is_executive=${isExecutive}`, {})
}
const getCorpStats = async (code: string): Promise<any> => {
	return API.get('Corporate', `/corporate_stats/${code}`, {})
}
const postUsers = async (emails: string[], code: string) => {
	return API.post('Corporate', `/codes/${code}/users`, {body: emails})
}
export const fetchIndividualStats = createAsyncThunk(
  'fetchIndividualStats',
  async (muid: string) => {
    try {
      const response = await API.get('Corporate', `/individual_user_stats/${muid}`, {})
      return response
    } catch (error) {
      console.error("Error fetching individual stats:", error)
      throw error
    }
  }
)

const resendInviteApiCall = async (
	code: string,
	muid: string,
): Promise<CorporateEmployee> => {
	return API.put('Corporate', `/codes/${code}/users/${muid}/resend`, {})
}

const deleteUser = async (muid: string, code: string) => {
	return API.del('Corporate', `/codes/${code}/users/${muid}`, {})
}

const deleteExecutiveUser = async (muid: string, code: string) => {
	return API.del('Corporate', `/codes/${code}/users/${muid}?removal_type=executive`, {})
}

const getCode = async () => {
	return API.get('Corporate', '/codes', {})
}

const inviteExecutiveUserAPI = async (muid: string, code: string, email: string) => {
	return API.post('Corporate', `/codes/${code}/executive/${muid}/send-invite`, {body: { email: email }})
}

export const inviteExecutiveUser = createAsyncThunk(
	'inviteExecutiveUser',
	async ({muid, code, email}: {muid: string, code: string, email: string}) => {
		try {
			const response = await inviteExecutiveUserAPI(muid, code, email)
			const response_message = response.detail || "Executive User invited successfully"
			Toast.show({
				type: 'success',
				text1: `${response_message}`,
			})
			return response
		} catch (error) {
			Logger.error(`Error inviting executive user email: ${error}`)
			const error_message = error.response.data.detail || "We were unable to invite the executive user"
			Toast.show({
				type: 'error',
				text1: `${error_message}`,
			})
		}
	},
)

const resendInviteExecutiveUserAPI = async (muid: string, code: string) => {
	return API.post('Corporate', `/codes/${code}/executive/${muid}/resend-invite`, {})
}

export const resendInviteExecutiveUser = createAsyncThunk(
	'inviteExecutiveUser',
	async ({muid, code}: {muid: string, code: string}) => {
		try {
			const response = await resendInviteExecutiveUserAPI(muid, code)
			const response_message = response.detail || "Executive User re-invited successfully"
			Toast.show({
				type: 'success',
				text1: `${response_message}`,
			})
			return response
		} catch (error) {
			Logger.error(`Error inviting executive user email: ${error}`)
			const error_message = error.response.data.detail || "We were unable to invite the executive user"
			Toast.show({
				type: 'error',
				text1: `${error_message}`,
			})
		}
	},
)

export const fetchUsers = createAsyncThunk(
	'fetchUsers',
	async (_, {dispatch}) => {
		try {
			const code = await dispatch(fetchCode()).unwrap()
			const users = await getUsers(code)
			return users
		} catch (error) {
			Logger.error(`Error fetching users: ${error}`)
			throw error
		}
	},
)
export const fetchUsersStats = createAsyncThunk(
	'fetchUsersStats',
	async (isExecutive: boolean, {dispatch}) => {
		try {
			const code = await dispatch(fetchCode()).unwrap()
			const usersstats = await getUserStats(isExecutive,code)
			return usersstats
		} catch (error) {
			Logger.error(`Error fetching users stats: ${error}`)
			throw error
		}
	},
)
export const fetchCorpStats = createAsyncThunk(
	'fetchCorpStats',
	async (_, {dispatch}) => {
		try {
			const code = await dispatch(fetchCode()).unwrap()
			const corpstats = await getCorpStats(code)
			return corpstats
		} catch (error) {
			Logger.error(`Error fetching users stats: ${error}`)
			throw error
		}
	},
)
export const postUserEmails = createAsyncThunk(
	'postUserEmails',
	async (payload: string[], {dispatch}) => {
		try {
			const code = await dispatch(fetchCode()).unwrap()
			const response = await postUsers(payload, code)
			const response_message = response.detail || "User added successfully"
			Toast.show({
				type: 'success',
				text1: `${response_message}`,
			})
			return response
		} catch (error) {
			Logger.error(`Error posting user emails: ${error}`)
			const error_message = error.response.data.detail || "We were unable to add the user"
			Toast.show({
				type: 'error',
				text1: `${error_message}`,
			})
		}
	},
)

export const removeUser = createAsyncThunk(
	'removeUser',
	async (payload: string, {dispatch}) => {
		try {
			const code = await dispatch(fetchCode()).unwrap()
			await deleteUser(payload, code)
			Toast.show({
				type: 'success',
				text1: 'User Removed Sucessfully',
			  })
			return
		} catch (error) {
			Logger.error(`Error deleting user ${payload} : ${error}`)
			Toast.show({
				type: 'error',
				text1: 'We were unable to remove that user',
			})
			throw error
		}
	},
)

export const removeExecutiveUser = createAsyncThunk(
	'removeExecutiveUser',
	async (payload: string, {dispatch}) => {
		try {
			const code = await dispatch(fetchCode()).unwrap()
			const res = await deleteExecutiveUser(payload, code)
			Toast.show({
				type: 'success',
				text1: 'User Removed Sucessfully',
			})
			return res
		} catch (error) {
			Logger.error(`Error deleting user ${payload} : ${error}`)
			Toast.show({
				type: 'error',
				text1: 'We were unable to remove that user',
			})
			throw error
		}
	},
)

export const resendInvite = createAsyncThunk(
	'resendInvite',
	async (payload: string, {dispatch}) => {
		try {
			const code = await dispatch(fetchCode()).unwrap()
			const employee = await resendInviteApiCall(code, payload)
			Toast.show({type: 'success', text1: 'Invitation successfully re-sent'})
			return employee
		} catch (error) {
			Logger.error(`Error resending invitation ${payload}: ${error}`)
			Toast.show({
				type: 'error',
				text1: 'We could not resend the invitation',
				text2: 'Please try again later',
			})
			throw error
		}
	},
)

export const fetchCode = createAsyncThunk(
	'fetchCode',
	async (_, {getState, dispatch}) => {
		const state = getState() as RootState
		const maybeCode = state.corporateService.code?.code
		if (maybeCode) {
			return maybeCode
		} else {
			try {
				const code = await getCode()
				dispatch(CorporateActions.populateCode(code))
				return code.code
			} catch (error) {
				if (error?.response?.status === 404) {
					Logger.info('Unable to find corporate code')
				} else {
					Logger.error(`Error getting corporate code: ${error}`)
				}
				throw error
			}
		}
	},
)

const {actions, reducer} = createSlice({
	name: 'corporateService',
	initialState: initialCorporateState,
	reducers: {
		populateUserStats: (
			state,
			{payload}: { payload: CorporateUserStats },
		) => {
			console.log('PAYLOFHD:: ')
			state.UserStatus = payload
		},
		populateCorpStats: (
			state,
			{payload}: { payload: CorpStats },
		) => {
			state.CorpStats = payload
		},
		populateEmployees: (
			state,
			{payload}: { payload: CorporateEmployee[] },
		) => {
			state.employees = payload
		},
		populateCode: (state, {payload}: { payload: CorporateCode }) => {
			state.code = payload
		},
		setModalOpen: (state, {payload}: { payload: boolean }) => {
			state.modalOpen = payload
		},
		populateExecutiveEmployees: (
			state,
			{payload}: { payload: ExecutiveCorporateEmployee[] },
		) => {
			state.executiveEmployees = payload
		},
	},
	extraReducers: (builder) => {
		builder.addCase(resendInvite.fulfilled, (state, action) => {
			const employeeIdx = state.employees.findIndex(
				(e) => e.muid === action.payload.muid,
			)
			if (employeeIdx >= 0) {
				state.employees = [
					...state.employees.slice(0, employeeIdx),
					action.payload,
					...state.employees.slice(employeeIdx + 1),
				]
			} else {
				Logger.error(
					`Unable to find update for resent invitation: ${action.payload.muid}`,
				)
			}
		})
		builder.addCase(fetchUsers.fulfilled, (state, {payload}) => {
			state.employees = payload
		})
		builder.addCase(fetchUsers.rejected, (state) => {
			state.employees = []
		})
		builder.addCase(fetchUsersStats.fulfilled, (state, {payload}) => {
			state.UserStatus = payload
		})
		builder.addCase(fetchUsersStats.rejected, (state) => {
			state.UserStatus = initialCorporateState.UserStatus
		})
		builder.addCase(fetchCorpStats.fulfilled, (state, {payload}) => {
			state.CorpStats = payload
		})
		builder.addCase(fetchCorpStats.rejected, (state) => {
			state.CorpStats = initialCorporateState.CorpStats
		})
		builder.addCase(FetchExecutiveUsers.fulfilled, (state, {payload}) => {
			state.executiveEmployees = payload
		})  
		builder.addCase(FetchExecutiveUsers.rejected, (state) => {
			state.executiveEmployees = []
		})
	},
})

export const CorporateActions = actions
export const CorporateReducer = reducer

export const EmployeesSelector = () =>
	useSelector((state: RootState) => state.corporateService.employees)
export const UserStatusSelector = () =>
	useSelector((state: RootState) => state.corporateService.UserStatus)
export const CorpStatusSelector = () =>
  useSelector((state: RootState) => state.corporateService.CorpStats)
export const ActiveEmployeesSelector = () => {
	return useSelector((state: RootState) =>
		state.corporateService.employees.filter(
			(employee) => employee.invite_status === 'accepted',
		),
	)
}
export const InvitedEmployeesSelector = () => {
	return useSelector((state: RootState) =>
		state.corporateService.employees.filter(
			(employee) =>
				employee.invite_status === 'invited' ||
				employee.invite_status === 'queued',
		),
	)
}
export const RemovedEmployeesSelector = () => {
	return useSelector((state: RootState) =>
		state.corporateService.employees.filter(
			(employee) =>
				employee.invite_status === 'deleted' ||
				employee.invite_status === 'failed' ||
				employee.invite_status === 'expired',
		),
	)
}
export const InvitedAndActiveEmployeesSelector = () => {
	return useSelector((state: RootState) =>
		state.corporateService.employees.filter(
			(employee) =>
				employee.invite_status !== 'deleted' &&
				employee.invite_status !== 'failed'
		),
	)
}
export const ModalOpenSelector = () =>
	useSelector((state: RootState) => state.corporateService.modalOpen)
export const SelectCorporateCode = () =>
	useAppSelector((state) => state.corporateService.code)
export const PriceSelector = () =>
	useAppSelector((state) =>
		state.corporateService.code?.cost_per_employee_cents
			? state.corporateService.code?.cost_per_employee_cents / 100
			: MEPRISM_PRICING_INFO.ANNUAL_EMPLOYEE_COST_DOLLARS,
	)

const selectExecutiveEmployees = (state: RootState) => state.corporateService.executiveEmployees;

export const ExecutiveEmployeesSelector = createSelector(
  [selectExecutiveEmployees],
  (executiveEmployees) => executiveEmployees
);

export const SelectAccountType = () =>
	useAppSelector((state) => state.corporateProfile.profile?.account_type)

export const SelectCompanySize = () =>
	useAppSelector((state) => state.corporateProfile.profile?.numberOfEmployees? state.corporateProfile.profile?.numberOfEmployees: 0)
